/**
 * SEO component that queries for data with
 *  Gatsby's useStaticQuery React hook
 *
 * See: https://www.gatsbyjs.org/docs/use-static-query/
 */
import { graphql, useStaticQuery } from "gatsby";
import React from "react";

import avatar from "../images/avatar-322x600.png";

import { SiteMetadataQueryResult } from "../graphql";
import { isoDatetime } from "../util/dates";
import { getImageURL, getMIMEType } from "../util/urls";
import { FeedPath, MASK_IMAGE_COLOR, StaticPath } from "../util/urls";

export interface OGMediaProps {
  url: string;
  width?: number;
  height?: number;
  alt?: string;
}

// There's more https://ogp.me/
// TODO: define race OG metadata
export enum OGType {
  Article = "article",
  Book = "book",
  Website = "website",
}

export interface TitleProps {
  description?: string;
  title?: string;
  permalink?: string;
  image?: string;
  audio?: string;
  video?: string;
}

export interface ArticleProps {
  type: OGType.Article;
  // there are more
  published?: string;
}

export interface SiteProps {
  type?: OGType.Website;
}

export type MetaElementsProps = React.PropsWithChildren<
  TitleProps & (ArticleProps | SiteProps)
>;

function isArticle(props: MetaElementsProps): props is ArticleProps {
  return props.type == OGType.Article;
}

export const OGArticle: React.FC<ArticleProps> = ({ published }) => (
  <>
    {published && (
      <meta
        property="article:published_time"
        content={isoDatetime(published)}
      />
    )}
  </>
);

export const OGImage: React.FC<OGMediaProps> = ({
  url,
  width,
  height,
  alt,
}) => {
  const imageURL = getImageURL(url);
  return (
    <>
      <meta property="og:image" content={imageURL} />
      <meta property="og:image:secure_url" content={imageURL} />
      <meta property="og:image:type" content={getMIMEType(imageURL)} />
      {width && <meta property="og:image:width" content={width.toString(10)} />}
      {height && (
        <meta property="og:image:height" content={height.toString(10)} />
      )}
      {alt && <meta property="og:image:alt" content={alt} />}
    </>
  );
};

export const OGVideo: React.FC<OGMediaProps> = ({ url, width, height }) => (
  <>
    <meta property="og:video" content={url} />
    <meta property="og:video:secure_url" content={url} />
    <meta property="og:video:type" content={getMIMEType(url)} />
    {width && <meta property="og:video:width" content={width.toString(10)} />}
    {height && (
      <meta property="og:video:height" content={height.toString(10)} />
    )}
  </>
);

export const OGAudio: React.FC<{ url: string }> = ({ url }) => (
  <>
    <meta property="og:audio" content={url} />
    <meta property="og:audio:secure_url" content={url} />
    <meta property="og:audio:type" content={getMIMEType(url)} />
  </>
);

const MetaElements: React.FC<MetaElementsProps> = ({
  title,
  permalink,
  image = avatar,
  audio,
  video,
  ...props
}) => {
  const query = useStaticQuery<SiteMetadataQueryResult>(graphql`
    query SiteMetadata {
      site {
        siteMetadata {
          author
          description
          language
          siteUrl
          title
        }
      }
    }
  `);

  const siteMetadata = query.site.siteMetadata;
  const description = props.description ?? siteMetadata.description;
  return (
    <>
      <html lang={siteMetadata.language} />
      <meta charSet="utf-8" />
      <meta httpEquiv="x-ua-compatible" content="ie=edge" />
      <title>
        {title ? (
          <>
            {title} | {siteMetadata.title}
          </>
        ) : (
          siteMetadata.title
        )}
      </title>
      <meta
        name="viewport"
        content="width=device-width, initial-scale=1, shrink-to-fit=no"
      />
      <meta name="application-name" content={siteMetadata.title} />
      <meta name="author" content={siteMetadata.author} />
      <meta name="color-scheme" content="only light" />
      <meta
        property="og:locale"
        content={siteMetadata.language.replace("-", "_")}
      />
      <meta property="og:site_name" content={siteMetadata.title} />
      {/* TODO: set theme color base on CSS*/}
      <meta name="theme-color" content={MASK_IMAGE_COLOR} />
      <link
        rel="alternate"
        type="application/rss+xml"
        href={FeedPath.RSS}
        title="RSS Feed"
      />
      <link
        rel="alternate"
        type="application/atom+xml"
        href={FeedPath.ATOM}
        title="Atom Feed"
      />
      <link
        rel="alternate"
        type="application/json"
        href={FeedPath.JSON}
        title="JSON Feed"
      />
      <link
        rel="mask-icon"
        href={StaticPath.ICON_MASK_IMAGE}
        color={MASK_IMAGE_COLOR}
      />
      <link rel="icon" href={StaticPath.HIGH_QUALITY_ICON} />
      <link type="text/plain" rel="author" href={StaticPath.HUMANS_TXT} />

      <meta name="description" content={description} />
      <meta property="og:title" content={title ?? siteMetadata.title} />
      <meta property="og:description" content={description} />
      {permalink && (
        <>
          <link rel="canonical" href={permalink} />
          <meta property="og:url" content={permalink} />
        </>
      )}
      <meta property="og:type" content={props.type} />
      <OGImage url={image} />
      {audio && <OGAudio url={audio} />}
      {video && <OGVideo url={video} />}
      {isArticle(props) && props.published && (
        <meta
          property="article:published_time"
          content={isoDatetime(props.published)}
        />
      )}
      {props.children}
    </>
  );
};

export default MetaElements;
